import { useThree } from '@react-three/fiber'
import { useAppStore } from '@store'
import { toVector3 } from '@utils/conversion/toVector3'
import CameraControls from 'camera-controls'
import { useCallback, useEffect, useRef } from 'react'
import { PerspectiveCamera } from 'three'

//TODO tipar controls con tipos de camera-controls
export function useCameraControls3D() {
  const cameraRef = useRef<PerspectiveCamera>(null)
  const controlsRef = useRef<CameraControls>(null)
  const isModalOpen = useAppStore.use.isModalOpen()
  const mode = useAppStore.use.mode()
  const walls = Array.from(useAppStore((s) => s.items).values()).filter(
    (item) => item.type === 'walls',
  )
  const cameraCenter = useAppStore.use.cameraCenter()
  const { controls } = useThree()
  const isSaving = useAppStore.use.isSaving()
  const setIsSaving = useAppStore.use.setIsSaving()

  const triggerCameraLogic = useCallback(() => {
    const currentWallIndex = 0
    if (isModalOpen) return
    const wall = walls[currentWallIndex]
    if (!wall) return
    const start = wall.start
    const end = wall.end
    const roomCenter = toVector3([cameraCenter[0], wall.height / 2, cameraCenter[2]])
    const wallCenter = toVector3([
      (start[0] + end[0]) / 2,
      wall.height / 2,
      (start[1] + end[1]) / 2,
    ])
    const directionToRoomCenter = toVector3([
      roomCenter.x - wallCenter.x,
      0,
      roomCenter.z - wallCenter.z,
    ]).normalize()
    const offset = 2000
    const rotationInRadians = (wall.rotation * Math.PI) / 180
    const cameraPosition = toVector3([
      wallCenter.x - directionToRoomCenter.x * offset * Math.cos(rotationInRadians),
      wall.height / 2,
      wallCenter.z - directionToRoomCenter.z * offset * Math.sin(rotationInRadians),
    ])
    controls?.setPosition(cameraCenter[0], cameraPosition.y, cameraCenter[2])
    controls?.moveTo(wallCenter.x, wall.height / 2, wallCenter.z, false)
    controls?.setPosition(cameraCenter[0], cameraPosition.y, cameraCenter[2])
    controls?.moveTo(wallCenter.x, wall.height / 2, wallCenter.z, false)
    controls?.forward(-0.1, true)
  }, [controls, cameraCenter, walls, isModalOpen])

  const listenToKeys = useCallback(
    ({ key }: KeyboardEvent) => {
      switch (key) {
        case '+':
          controls?.dolly(100, true)
          break
        case '-':
          controls?.dolly(-100, true)
          break
        case 'ArrowUp':
          mode === 'MODE_3D_VIEW' ? controls?.forward(0.1, true) : controls?.forward(50, true)
          break
        case 'ArrowDown':
          mode === 'MODE_3D_VIEW' ? controls?.forward(-0.1, true) : controls?.forward(-50, true)
          break
        case 'ArrowLeft':
          controls?.truck(-100, 0, true)
          break
        case 'ArrowRight':
          controls?.truck(100, 0, true)
          break
        case 'a':
          controls?.truck(-100, 0, true)
          break
        case 'w':
          mode === 'MODE_3D_VIEW' ? controls?.forward(0.1, true) : controls?.forward(50, true)
          break
        case 's':
          mode === 'MODE_3D_VIEW' ? controls?.forward(-0.1, true) : controls?.forward(-50, true)
          break
        case 'd':
          controls?.truck(100, 0, true)
          break
        case ' ':
          triggerCameraLogic()
          break
      }
    },
    [controls, cameraCenter, walls, isModalOpen, mode, triggerCameraLogic],
  )

  useEffect(() => {
    if (controls) window.addEventListener('keydown', listenToKeys)
    return () => {
      if (controls) window.removeEventListener('keydown', listenToKeys)
    }
  }, [controls, listenToKeys, isModalOpen])

  useEffect(() => {
    if (isSaving) {
      triggerCameraLogic()
      setIsSaving(false)
    }
  }, [isSaving, triggerCameraLogic, setIsSaving])

  return { cameraRef, controlsRef, listenToKeys }
}
